import graphql from "babel-plugin-relay/macro";
import { Suspense } from "react";
import { Platform } from "react-native";
import { useLazyLoadQuery } from "react-relay";

import { ApplePayButton } from "./ApplePayButton";
import { FacilityApplePayButtonFacilityQuery } from "./__generated__/FacilityApplePayButtonFacilityQuery.graphql";
import { FacilityApplePayButtonSessionQuery } from "./__generated__/FacilityApplePayButtonSessionQuery.graphql";
import type { ApplePayButtonProps } from "./types";

type FacilityApplePayButtonProps = Omit<
  ApplePayButtonProps,
  "countryCode" | "currencyCode" | "locationId"
> & {
  facilityId: string;
  bookingId?: string;
};

/** Resolves the authorized booking's original checkout location, or public facility checkout for other flows. */
export const FacilityApplePayButton = (props: FacilityApplePayButtonProps) => {
  if (Platform.OS === "android") {
    return null;
  }

  return (
    <Suspense fallback={null}>
      {props.bookingId ? (
        <FacilityApplePayButtonContent {...props} />
      ) : (
        <CustomerOnlyApplePayButton {...props} />
      )}
    </Suspense>
  );
};

const CustomerOnlyApplePayButton = (props: FacilityApplePayButtonProps) => {
  const data = useLazyLoadQuery<FacilityApplePayButtonSessionQuery>(
    FacilityApplePayButtonSessionQueryString,
    {},
    { fetchPolicy: "store-or-network" }
  );

  if (data.session?.currentClientProfile?.member?.__typename !== "Customer") {
    return null;
  }

  return <FacilityApplePayButtonContent {...props} />;
};

const FacilityApplePayButtonContent = ({
  facilityId,
  bookingId,
  ...applePayButtonProps
}: FacilityApplePayButtonProps) => {
  const data = useLazyLoadQuery<FacilityApplePayButtonFacilityQuery>(
    FacilityApplePayButtonFacilityQueryString,
    { facilityId, bookingId: bookingId ?? facilityId, hasBooking: !!bookingId },
    { fetchPolicy: "store-or-network" }
  );
  const facility =
    data.facility?.__typename === "Facility" ? data.facility : null;
  const booking =
    data.booking && "paymentRequirements" in data.booking ? data.booking : null;

  if (bookingId && booking?.bookingAudience !== "CUSTOMER") {
    return null;
  }

  const locationId = bookingId
    ? booking?.paymentRequirements?.checkoutLocationId
    : facility?.squarePosConfiguration?.locationId;

  if (!locationId || !facility) {
    return null;
  }

  return (
    <ApplePayButton
      {...applePayButtonProps}
      locationId={locationId}
      countryCode={facility.club?.defaultCountryCode ?? undefined}
      currencyCode={facility.club?.currency ?? undefined}
    />
  );
};

const FacilityApplePayButtonSessionQueryString = graphql`
  query FacilityApplePayButtonSessionQuery {
    session {
      currentClientProfile {
        member {
          __typename
        }
      }
    }
  }
`;

const FacilityApplePayButtonFacilityQueryString = graphql`
  query FacilityApplePayButtonFacilityQuery(
    $facilityId: ID!
    $bookingId: ID!
    $hasBooking: Boolean!
  ) {
    booking: node(id: $bookingId) @include(if: $hasBooking) {
      ... on Booking {
        bookingAudience
        paymentRequirements {
          checkoutLocationId
        }
      }
    }
    facility: node(id: $facilityId) {
      __typename
      ... on Facility {
        squarePosConfiguration @skip(if: $hasBooking) {
          locationId
        }
        club {
          currency
          defaultCountryCode
        }
      }
    }
  }
`;
